/**
 * 埋め込みコードの生成（FR-LINK-04）。
 *
 * iframe に入る文書を配信するのは導入者のサイトであって、このライブラリでも
 * サーバでもない。したがって `frame-ancestors` / `X-Frame-Options` を出せるのは
 * 導入者側であり、ここの許可リストはセキュリティ境界ではない — 生成側のガード兼
 * **正規化表**である（例: ステージングで開いても本番ドメインのスニペットを出す）。
 *
 *   1. base = share.embedBase（空なら現在ページの URL）
 *   2. 許可リストが空                  → base をそのまま使う
 *   3. base の origin が許可リストにある → base をそのまま使う
 *   4. 無い                            → origin を許可リストの先頭へ差し替える
 */
import { BRAND_NAME } from '../brand';
import { applyHash, type HashState } from './hash-codec';

export interface EmbedOptions {
  /** 埋め込み先の基準 URL。空なら現在ページ。 */
  base: string;
  /** 埋め込みコードに出してよい配信元。空 = 制限しない。 */
  allowlist: readonly string[];
  width: string;
  height: string;
}

export function buildEmbedCode(
  view: Window,
  prefix: string,
  state: HashState,
  options: EmbedOptions,
): string {
  const src = embedUrl(view, prefix, state, options);
  const attributes = [
    `src="${escapeAttribute(src)}"`,
    `width="${escapeAttribute(options.width)}"`,
    `height="${escapeAttribute(options.height)}"`,
    'style="border:0"',
    'loading="lazy"',
    'allowfullscreen',
    `title="${escapeAttribute(BRAND_NAME)}"`,
  ];
  return `<iframe ${attributes.join(' ')}></iframe>`;
}

/** スニペットの src（許可リストによる正規化を適用したうえでハッシュを載せる）。 */
export function embedUrl(
  view: Window,
  prefix: string,
  state: HashState,
  options: EmbedOptions,
): string {
  const current = `${view.location.origin}${view.location.pathname}${view.location.search}`;
  const base = normalizeOrigin(options.base.trim() || current, options.allowlist);
  const index = base.indexOf('#');
  const withoutHash = index < 0 ? base : base.slice(0, index);
  const hash = index < 0 ? '' : base.slice(index);
  return `${withoutHash}${applyHash(hash, prefix, state)}`;
}

function normalizeOrigin(url: string, allowlist: readonly string[]): string {
  const allowed = allowlist.map((entry) => entry.trim()).filter((entry) => entry !== '');
  if (allowed.length === 0) return url;

  const origin = originOf(url);
  if (origin !== null && allowed.some((entry) => originOf(entry) === origin)) return url;

  const fallback = originOf(allowed[0] as string);
  if (fallback === null || origin === null) return url;
  return fallback + url.slice(origin.length);
}

/** URL 文字列の origin。解釈できなければ null（そのときは書き換えない）。 */
function originOf(raw: string): string | null {
  const match = /^(https?:\/\/[^/?#]+)/i.exec(raw.trim());
  return match ? (match[1] as string) : null;
}

/** 属性値へ埋める文字列のエスケープ（NFR-SEC-02）。 */
function escapeAttribute(raw: string): string {
  return raw
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}
